// react
import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useHistory } from 'react-router'

// components
import { Button, BUTTON_SIZE, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'
import Header from '_components/header'
import MoreInfoDropdown from '_components/more-info-dropdown'
import ListView from '_components/list-view'
import CreateTaxonomy from './taxonomy/create'
import ModalCloneTaxonomy from './modal-clone-taxonomy'
import BatchSidebar, { ACTIONS, clearBatchSidebar } from '_components/batch-sidebar'

// lib
import Events from '@st-operations/uikit/Events.js'
import Storage from '@st-operations/uikit/Storage'
import bulkUpdate from '_lib/bulkUpdate'

// services
import { cloneTaxonomy } from '_services/taxonomies'

const TAXONOMIES_TYPES = [
  { label: 'Branded Taxonomy', value: 'true' },
  { label: 'White Label Taxonomy', value: 'false' },
]

const TaxonomiesPage = () => {
  const history = useHistory()
  const [state, _setState] = useState({
    batch_percentage: 0,
    batch_size: 0,
    error: null,
    show_info: false,
    show_create_page: false,
    taxonomies: [],
    remountKey: 0,
    is_modal_clone_open: false,
    selected_taxonomies: [],
  })

  const setState = (newState) => _setState((prevState) => ({ ...prevState, ...newState }))

  const listViewItemsRef = useRef([])

  const softRefresh = (error) => !error && setState({ remountKey: Math.random() })

  const startBatchProcess = (ids) => {
    Storage.set('abort_batch', false)
    Events.emit('set_batch_process', true)
    setState({ batch_percentage: 0, batch_size: ids.length ?? ids.size })
  }

  const updateBatchProgress = () => {
    _setState((prevState) => ({
      ...prevState,
      batch_percentage: prevState.batch_percentage + 1 / prevState.batch_size,
    }))
  }

  const handleCloseInfo = () => {
    setState({ show_info: false })
  }

  const handleSelectionChange = ({ action, items }) => {
    const selectedEntities = listViewItemsRef.current.filter(({ _id }) => items.includes(_id))
    Events.emit('ListView-selection', { action, items, entities: selectedEntities })
  }

  const toggleCreateTaxonomyForm = () => {
    setState({ show_create_page: !state.show_create_page })
  }

  const deleteTaxonomy = (items) => {
    const ids = items.map(({ _id }) => _id)
    startBatchProcess(ids)
    let bulkAction = {
      ids,
      method: 'delete',
      url: '/taxonomies',
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const openModalClone = (items) => {
    setState({ is_modal_clone_open: true, selected_taxonomies: items })
  }

  const closeModalClone = () => {
    setState({ is_modal_clone_open: false })
  }

  const cloneTaxonomies = async (taxonomiesMap) => {
    const taxonomies = Object.values(taxonomiesMap)
    startBatchProcess(taxonomies.map(({ _id }) => _id))

    let cloned = 0
    for (const taxonomy of taxonomies) {
      const name = taxonomy.name?.trim()
      const slug = taxonomy.slug?.trim()

      if (!name) {
        toast.error("missing 'name'")
        updateBatchProgress()
        continue
      }

      const payload = { name, ...(slug && { slug }) }

      try {
        const { clonedAudiences } = await cloneTaxonomy(taxonomy._id, payload)
        cloned += 1
        toast.success(`Cloned "${name}" with ${clonedAudiences} audiences`)
      } catch (error) {
        toast.error(error.message)
      }
      updateBatchProgress()
    }

    clearBatchSidebar()
    if (cloned) softRefresh()
  }

  const handleBatchSidebarAction = (action, items) => {
    switch (action) {
      case ACTIONS.CLONE_WITH_AUDIENCES:
        return openModalClone(items)
      case ACTIONS.DELETE:
        return deleteTaxonomy(items)
    }
  }

  const renderTaxonomyListOrCreate = () => {
    const { show_create_page } = state

    if (show_create_page) {
      return <CreateTaxonomy onToggle={toggleCreateTaxonomyForm} />
    }

    return (
      <div id="taxonomies" key={state.remountKey}>
        <If condition={state.error}>
          <p>{state.error.message}</p>
        </If>

        <Header
          title="Taxonomies"
          actions={[
            { children: 'Create New Taxonomy', onClick: toggleCreateTaxonomyForm },
            {
              theme: BUTTON_THEME.GHOST,
              children: (
                <Icon
                  type="outline"
                  icon="info"
                  color="#13682B"
                  className="header__button"
                  width="24px"
                  height="24px"
                />
              ),
              onClick: () =>
                _setState((prevState) => ({ ...prevState, show_info: !prevState.show_info })),
            },
          ]}
        />
        <If condition={state.show_info}>
          <MoreInfoDropdown onClose={handleCloseInfo} />
        </If>

        <div className="batch-sidebar__list-view-wrapper">
          <ListView
            className="st-list-view--has-sidebar"
            showCheckboxes={true}
            onSelectionChange={handleSelectionChange}
            batch_percentage={state.batch_percentage}
            batch_size={state.batch_size}
            itemsRef={listViewItemsRef}
            columns={[
              {
                key: 'name',
                sortable: true,
                render: (item) => {
                  const hasNoDistribution = !item.distribution?.length
                  return (
                    <Link
                      to={`/taxonomies/${item.slug}/audiences`}
                      key={`taxonomy-name-item-${item.index}`}
                      className={hasNoDistribution ? 'line-through text-gray-400' : ''}
                    >
                      {item.name}
                    </Link>
                  )
                },
              },
              {
                key: 'is_branded',
                name: 'Type',
                render: (item) => {
                  let text = 'White Label Taxonomy'
                  if (item.is_branded) {
                    text = 'Branded Taxonomy'
                  }
                  return (
                    <div key={`taxonomy-branded-item-${item.index}`}>
                      <span>{text}</span>
                    </div>
                  )
                },
              },
              {
                // the two ways a taxonomy's audiences go out to partners - as
                // files, or over the partner's API
                key: 'actions',
                name: '',
                render: (item) => (
                  <div
                    className="flex justify-end gap-2 whitespace-nowrap"
                    key={`taxonomy-actions-${item.index}`}
                  >
                    <Button
                      theme={BUTTON_THEME.SECONDARY}
                      size={BUTTON_SIZE.SMALL}
                      onClick={() => history.push(`/taxonomies/${item._id}`)}
                    >
                      Distributions
                    </Button>
                    <Button
                      theme={BUTTON_THEME.SECONDARY}
                      size={BUTTON_SIZE.SMALL}
                      onClick={() => history.push(`/taxonomies/${item.slug}/api-integrations`)}
                    >
                      API Integrations
                    </Button>
                  </div>
                ),
              },
            ]}
            customPlaceholder="Search for Taxonomies"
            filters={[
              {
                name: 'is_branded',
                label: 'All Taxonomy Types',
                options: TAXONOMIES_TYPES,
                all: true,
              },
            ]}
            entities="taxonomies"
            filter_on_load={{ is_branded: { label: 'Branded Taxonomy', value: 'true' } }}
            fields={['name', 'is_branded', 'slug', 'distribution']}
            sort_key={'name'}
            sort_dir={1}
          />
          <BatchSidebar
            onConfirm={handleBatchSidebarAction}
            actions={[ACTIONS.CLONE_WITH_AUDIENCES, ACTIONS.DELETE]}
          />
        </div>

        <ModalCloneTaxonomy
          selectedTaxonomies={state.selected_taxonomies}
          isModalCloneOpen={state.is_modal_clone_open}
          clone={cloneTaxonomies}
          closeModal={closeModalClone}
        />
      </div>
    )
  }

  return <>{renderTaxonomyListOrCreate()}</>
}

export default TaxonomiesPage
